import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { decode } from 'he';
import { GET_TOPICS, GET_MEDIA_ASSETS } from '../../graphql';
import { CreateTopicModal } from '../topics/CreateTopicModal';
import { useAccount } from '../../context/AccountContext';
import { MediaLibrary } from './MediaLibrary';

type LibraryFilter = 'all' | 'review' | 'draft' | 'scheduled' | 'live';

interface ContentTopic {
  id: string;
  title?: string | null;
  summary?: string | null;
  publishedDate?: string | null;
  publishStatus?: string | null;
  contentType?: 'webpage' | 'video' | 'image' | 'article' | null;
  category?: string | null;
  atomizedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  verifiedFacts?: unknown[] | null;
  dataSource?: { id: string; name: string; type: string } | null;
}

/**
 * One status per record, so the chip counts add up to All.
 * Needs review is published work that has not been atomized yet.
 */
function libraryFilter(topic: ContentTopic): Exclude<LibraryFilter, 'all'> {
  const status = topic.publishStatus || 'draft';
  if (status === 'scheduled') return 'scheduled';
  if (status === 'draft') return 'draft';
  if (!topic.atomizedAt) return 'review';
  return 'live';
}

function statusLabel(topic: ContentTopic) {
  const bucket = libraryFilter(topic);
  if (bucket === 'review') return 'Needs review';
  if (bucket === 'scheduled') return 'Scheduled';
  if (bucket === 'draft') return 'Draft';
  return 'Live';
}

interface GetTopicsResp {
  topics: ContentTopic[];
}

const STATUS_STYLE: Record<Exclude<LibraryFilter, 'all'>, { dot: string; text: string }> = {
  live: { dot: 'var(--ink-green)', text: 'var(--ink-ink)' },
  review: { dot: 'var(--ink-amber)', text: 'var(--ink-amber)' },
  draft: { dot: 'var(--ink-ink3)', text: 'var(--ink-ink2)' },
  scheduled: { dot: 'var(--ink-accent)', text: 'var(--ink-ink)' },
};

/** Short codes are stored lowercase. The library shows them as NCAAF, MLB. */
function topicLabel(value?: string | null) {
  const raw = (value || '').trim();
  if (!raw) return '—';
  if (/^[a-z0-9]{1,8}$/i.test(raw)) return raw.toUpperCase();
  return raw;
}

function stamp(topic: ContentTopic) {
  return topic.updatedAt || topic.publishedDate || topic.createdAt || null;
}

function clock(iso: string) {
  const d = new Date(iso);
  return d.getMinutes() === 0 ? format(d, 'h a') : format(d, 'h:mm a');
}

function rowTime(topic: ContentTopic) {
  const iso = stamp(topic);
  if (!iso) return '—';
  const time = clock(iso);
  if (libraryFilter(topic) === 'scheduled') return `${format(new Date(iso), 'EEE')} ${time}`;
  return time;
}

function dayBucket(topic: ContentTopic) {
  if (libraryFilter(topic) === 'scheduled')
    return { key: 'scheduled', label: 'SCHEDULED', order: 0 };
  const iso = stamp(topic);
  if (!iso) return { key: 'earlier', label: 'EARLIER', order: 100000 };
  const d = new Date(iso);
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((start(new Date()) - start(d)) / 86400000);
  const dateLabel = format(d, 'MMM d').toUpperCase();
  if (diff === 0) return { key: 'today', label: `TODAY · ${dateLabel}`, order: 1 };
  if (diff === 1) return { key: 'yesterday', label: `YESTERDAY · ${dateLabel}`, order: 2 };
  return { key: `day-${start(d)}`, label: dateLabel, order: 3 + Math.max(diff, 0) };
}

/** Articles written in the same 10 minutes by the same source are one publish run. */
function batchKey(topic: ContentTopic) {
  const iso = topic.createdAt || topic.updatedAt || topic.publishedDate;
  if (!iso) return null;
  const bucket = Math.floor(new Date(iso).getTime() / 600000);
  return `${topic.dataSource?.id || 'manual'}|${bucket}`;
}

function markFor(name?: string | null) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] || 'MC').slice(0, 2).toUpperCase();
}

function factLabel(topic: ContentTopic) {
  const n = topic.verifiedFacts?.length || 0;
  if (!n) return null;
  return n === 1 ? '1 fact' : `${n} facts`;
}

const FILTERS: Array<{ id: LibraryFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'review', label: 'Needs review' },
  { id: 'draft', label: 'Drafts' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'live', label: 'Live' },
];

export function ContentLayout() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'media' ? 'media' : 'articles';
  const [search, setSearch] = useState<string>('');
  const [filter, setFilter] = useState<LibraryFilter>('all');
  const [topicsOn, setTopicsOn] = useState<Set<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const uploadMedia = useRef<(() => void) | null>(null);
  const [openBatches, setOpenBatches] = useState<Record<string, boolean>>({});

  const { accountId } = useAccount();

  const { data: mediaData } = useQuery(GET_MEDIA_ASSETS, {
    variables: { accountId, limit: 1, offset: 0 },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const mediaCount: number = mediaData?.mediaAssets?.total ?? 0;

  const { data, loading } = useQuery<GetTopicsResp>(GET_TOPICS, {
    variables: { accountId },
    skip: !accountId,
  });

  const topics = useMemo(() => data?.topics || [], [data]);
  const counts = useMemo(() => {
    const c = { all: topics.length, review: 0, draft: 0, scheduled: 0, live: 0 };
    for (const topic of topics) c[libraryFilter(topic)] += 1;
    return c;
  }, [topics]);
  const categories = useMemo(() => {
    const names = new Set<string>();
    for (const topic of topics) {
      const name = (topic.category || '').trim();
      if (name) names.add(name);
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [topics]);

  const q = search.trim().toLowerCase();
  const items = topics
    .filter((t) => filter === 'all' || libraryFilter(t) === filter)
    .filter((t) => topicsOn.size === 0 || topicsOn.has((t.category || '').trim()))
    .filter((t) => (q ? `${t.title || ''} ${t.summary || ''}`.toLowerCase().includes(q) : true))
    .slice()
    .sort((a, b) => {
      const aTime = stamp(a) ? new Date(stamp(a) as string).getTime() : 0;
      const bTime = stamp(b) ? new Date(stamp(b) as string).getTime() : 0;
      return bTime - aTime;
    });

  const rows = useMemo(() => {
    const byDay = new Map<string, { label: string; order: number; items: ContentTopic[] }>();
    for (const topic of items) {
      const day = dayBucket(topic);
      const group = byDay.get(day.key) || { label: day.label, order: day.order, items: [] };
      group.items.push(topic);
      byDay.set(day.key, group);
    }
    const out: Array<
      | { kind: 'day'; key: string; label: string }
      | { kind: 'batch'; key: string; label: string; sub: string; time: string; mark: string }
      | { kind: 'more'; key: string; batch: string; label: string }
      | { kind: 'article'; topic: ContentTopic; indent: boolean }
    > = [];
    const days = [...byDay.entries()].sort((a, b) => a[1].order - b[1].order);
    for (const [dayKey, day] of days) {
      out.push({ kind: 'day', key: dayKey, label: day.label });
      const seen = new Set<string>();
      for (const topic of day.items) {
        if (seen.has(topic.id)) continue;
        const key = batchKey(topic);
        const mates = key ? day.items.filter((t) => batchKey(t) === key) : [topic];
        if (key && mates.length >= 4) {
          mates.forEach((t) => seen.add(t.id));
          const open = Boolean(openBatches[key]) || Boolean(q);
          const source = topic.dataSource?.name || 'an agent';
          const cats = [...new Set(mates.map((t) => (t.category || '').trim()).filter(Boolean))];
          out.push({
            kind: 'batch',
            key,
            label: `Batch of ${mates.length} published by ${source}`,
            sub: cats.length ? `· ${topicLabel(cats[0])}` : '',
            time: rowTime(topic),
            mark: markFor(source),
          });
          for (const mate of open ? mates : mates.slice(0, 3)) {
            out.push({ kind: 'article', topic: mate, indent: true });
          }
          if (!open && mates.length > 3) {
            out.push({
              kind: 'more',
              key: `${key}-more`,
              batch: key,
              label: `Show ${mates.length - 3} more from this batch`,
            });
          } else if (open && mates.length > 3 && !q) {
            out.push({ kind: 'more', key: `${key}-more`, batch: key, label: 'Collapse batch' });
          }
        } else {
          seen.add(topic.id);
          out.push({ kind: 'article', topic, indent: false });
        }
      }
    }
    return out;
  }, [items, openBatches, q]);

  const toggleBatch = (key: string) => {
    setOpenBatches((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setView = (next: 'articles' | 'media') => {
    if (next === 'media') setParams({ view: 'media' });
    else setParams({});
  };

  const toggleTopic = (name: string) => {
    setTopicsOn((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <div className="ink-page flex w-full flex-col h-full min-h-0 overflow-hidden">
      <div className="ink-scroll flex flex-col flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
        <div className="w-full max-w-full overflow-hidden relative flex-1 flex flex-col">
          <div className="ink-canvas m-0 w-full h-full relative overflow-hidden box-border flex flex-col">
            <div className="ink-frame">
              <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                <h1 className="ink-h1">Content</h1>
                <div className="ink-seg">
                  <button
                    type="button"
                    data-active={view === 'articles' ? 'true' : 'false'}
                    onClick={() => setView('articles')}
                  >
                    Articles{' '}
                    <span style={{ color: 'var(--ink-ink3)', fontWeight: 400 }}>{counts.all}</span>
                  </button>
                  <button
                    type="button"
                    data-active={view === 'media' ? 'true' : 'false'}
                    onClick={() => setView('media')}
                  >
                    Media{' '}
                    <span style={{ color: 'var(--ink-ink3)', fontWeight: 400 }}>
                      {mediaCount || ''}
                    </span>
                  </button>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <Link to="/content/settings" className="ink-btn ink-btn-quiet">
                    Content settings
                  </Link>
                  {view === 'articles' ? (
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      disabled={!accountId}
                      onClick={() => setCreateOpen(true)}
                    >
                      New article
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      disabled={!accountId}
                      onClick={() => uploadMedia.current?.()}
                    >
                      Upload
                    </button>
                  )}
                </div>
              </div>

              {view === 'media' ? (
                <MediaLibrary
                  embedded
                  onReady={(open) => {
                    uploadMedia.current = open;
                  }}
                />
              ) : (
                <>
                  <div className="ink-lib-filters">
                    <input
                      className="ink-search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search titles and summaries"
                    />
                    {FILTERS.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        className="ink-chip"
                        data-active={filter === f.id ? 'true' : 'false'}
                        onClick={() => setFilter(f.id)}
                      >
                        {f.label} <span>{counts[f.id]}</span>
                      </button>
                    ))}
                    {categories.length > 0 && <span className="ink-lib-rule" />}
                    {categories.map((name) => (
                      <button
                        key={name}
                        type="button"
                        className="ink-chip"
                        data-active={topicsOn.has(name) ? 'true' : 'false'}
                        onClick={() => toggleTopic(name)}
                      >
                        {topicLabel(name)}
                      </button>
                    ))}
                    <span className="ink-lib-count">
                      {items.length} article{items.length === 1 ? '' : 's'} · newest first
                    </span>
                  </div>

                  {accountId && (
                    <CreateTopicModal
                      isOpen={createOpen}
                      onClose={() => setCreateOpen(false)}
                      accountId={accountId}
                      onCreated={(id) => navigate(`/content/${id}`)}
                    />
                  )}

                  <div className="ink-scroll flex-1 min-h-0 overflow-auto">
                    {loading ? (
                      <p className="ink-muted" style={{ marginTop: 28 }}>
                        Loading…
                      </p>
                    ) : items.length === 0 ? (
                      <p className="ink-muted" style={{ padding: '48px 12px' }}>
                        No articles match. Try a different filter or search.
                      </p>
                    ) : (
                      <div>
                        <div className="ink-lib-head">
                          <span>Title</span>
                          <span>Topic</span>
                          <span>Status</span>
                          <span>Facts</span>
                          <span>Updated</span>
                        </div>
                        {rows.map((row) => {
                          if (row.kind === 'day') {
                            return (
                              <div key={row.key} className="ink-lib-day">
                                {row.label}
                              </div>
                            );
                          }
                          if (row.kind === 'batch') {
                            const open = Boolean(openBatches[row.key]) || Boolean(q);
                            return (
                              <div
                                key={row.key}
                                className="ink-lib-batch"
                                onClick={() => toggleBatch(row.key)}
                              >
                                <span className="ink-lib-chev">{open ? '▾' : '▸'}</span>
                                <span className="ink-lib-mark">{row.mark}</span>
                                <span>{row.label}</span>
                                {row.sub ? <span className="ink-lib-sub">{row.sub}</span> : null}
                                <span className="ink-lib-time">{row.time}</span>
                              </div>
                            );
                          }
                          if (row.kind === 'more') {
                            return (
                              <div
                                key={row.key}
                                className="ink-lib-more"
                                onClick={() => toggleBatch(row.batch)}
                              >
                                {row.label}
                              </div>
                            );
                          }
                          const topic = row.topic;
                          const bucket = libraryFilter(topic);
                          const tone = STATUS_STYLE[bucket];
                          const facts = factLabel(topic);
                          const summary = decode(topic.summary || '').trim();
                          return (
                            <div
                              key={topic.id}
                              className="ink-lib-row"
                              onClick={() => navigate(`/content/${topic.id}`)}
                            >
                              <div
                                className="ink-lib-main"
                                style={{ paddingLeft: row.indent ? 22 : 0 }}
                              >
                                <div className="ink-lib-title">
                                  {decode(topic.title || 'Untitled')}
                                </div>
                                {summary ? <div className="ink-lib-summary">{summary}</div> : null}
                              </div>
                              <span className="ink-lib-topic">{topicLabel(topic.category)}</span>
                              <span className="ink-lib-status" style={{ color: tone.text }}>
                                <span className="ink-dot" style={{ background: tone.dot }} />
                                {statusLabel(topic)}
                              </span>
                              <span
                                className="ink-lib-facts"
                                style={{ color: facts ? 'var(--ink-ink2)' : 'var(--ink-line3)' }}
                              >
                                {facts || '—'}
                              </span>
                              <span className="ink-lib-time">{rowTime(topic)}</span>
                            </div>
                          );
                        })}
                        <div className="ink-list-end">
                          <div className="ink-list-end-row">
                            <span className="ink-list-end-rule" />
                            <span className="ink-list-end-gem" />
                            <span className="ink-list-end-rule" />
                          </div>
                          <p className="ink-list-end-label">The end.</p>
                          <div className="ink-list-end-note">No more articles</div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
